// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, Inject } from '@angular/core';
import { Http, Headers, Response, RequestOptions } from '@angular/http';

import { Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, catchError, tap } from 'rxjs/operators';
import { API_KEY } from '../environment.configuration';

export interface HttpOptionalParams {
  queryParams?: QueryParams;
  urlParam?: any;
  urlMultiParams?: any[];
}

export interface QueryParams {
  name: string;
  value: any;
}

export const HTTP_BAD_REQUEST = 400;
export const HTTP_CONFLICT = 409;
export const HTTP_UNAUTHORIZED = 401;

@Injectable()
export class HttpService {
  constructor(private httpClient: HttpClient, @Inject(API_KEY) private api: string) {}

  public requestInProgress = false;

  public urls = {
    roles: {
      list: `${this.api}/roles`,
      role: `${this.api}/roles/-`,
      /**
       * GET a list of policies from a Role
       * Response: Interface RolePolicy[]
       */
      rolePolicies: `${this.api}/roles/-/policies`,
      /**
       *
       */
      rolePoliciesByPolicyId: `${this.api}/roles/-/policies/-`,
      /**
       * roleUsers: GET list of users from a role
       * Response : RoleUser[] Interface
       * e.g. roles/fb77bdcb-c799-4d15-8303-7c915ece46b1/users
       */
      roleUsers: `${this.api}/roles/-/users`,
      /**
       * roleUsers: GET list of user'scopes from a role
       * Response : RoleUserScopeReponse[] Interface
       *
       * e.g. /roles/fb77bdcb-c799-4d15-8303-7c915ece46b1/users/nicholascw/scopes
       */
      roleUserScopes: `${this.api}/roles/-/users/-/scopes`,
      /**
       * roleUserScope CREATE | EDIT user' scopes of a role
       * POST:  role-user scopes, Add users to a Role
       * Request body : RoleUserScopeRequest Interface
       */
      roleUserScope: `${this.api}/roleuserscope`,
      /**
       *
       */
      roleUserScopeByUsername: `${this.api}/roleuserscope/user`,
      roleScopeTypes: `${this.api}/roles/scopes/types`,
      /**
       * GET a list of scope by type
       * Response : interface ScopeConstraint[]
       *
       * e.g /roles/scopes/2
       */
      roleScopeConstraints: `${this.api}/roles/scopes/-`,
      /**
       * roleGroup
       * Actions: POST | DELETE
       * POST Request : AddGroupsToRoleRequest Interface
       * DELETE Request:  DeleteRoleGroupRequest Interface
       */
      roleGroup: `${this.api}/rolegroup`,
      /**
       * roleGroups GET LIST by RoleId
       * Response : RoleGroup[]
       */
      roleGroups: `${this.api}/roles/-/groups`,
      /**
       * roleGroupsByID
       * Action: DELETE a role's group
       */
      roleGroupsByID: `${this.api}/roles/-/groups/-`,
      // roleGroupScopes GET LIST By roleId , groupId
      // Response: RoleGroupScope[]
      roleGroupScopes: `${this.api}/roles/-/groups/-/scopes`
    },
    users: {
      // User in backend session
      /**
       * GET current user in session
       * Response: LoggedUserResponse Interface
       */
      currentUser: `${this.api}/user`,
      /**
       * list: GET list of Users
       * Response : { users: User[] } interface
       */
      list: `${this.api}/users`,
      /**
       * user: GET user by username
       * Response : UserByUsername interface
       */
      user: `${this.api}/users/-`,
      /**
       * GET userGroups : groups by username
       * Response : interface UserGroup[]
       *
       * POST : add users to group
       * request: UserGroupsCreateRequest
       */
      userGroups: `${this.api}/users/-/groups`,
      /**
       * userRoleScope: Get list of user's roles
       * Response: UserRole[] interface from LoggedUser Interfaces
       */
      userRoleScopes: `${this.api}/users/-/rolescopes`
    },
    groups: {
      list: `${this.api}/groups`,
      /**
       * groups GET list
       * Response: Group Interface
       */
      groups: `${this.api}/groups/-`,
      /**
       * DELETE a group's user e.g /v1/groups/{groupId}/users/{userGroupId}"
       *
       */
      groupUserRemove: `${this.api}/groups/-/users/-`,
      /**
       * groupUser GET list of users
       * Response: GroupUser Interface
       */
      groupUsers: `${this.api}/groups/-/users`
    }
  };

  serializeResourceURl(resourceUrl: string, optionalParams: HttpOptionalParams) {
    const { queryParams, urlMultiParams, urlParam } = optionalParams;

    const query = this._getQueryString(queryParams);

    if (urlMultiParams) {
      resourceUrl = this._setUrlMultiParams(resourceUrl, urlMultiParams);
    } else {
      resourceUrl = this._setUrlParam(resourceUrl, urlParam);
    }

    return `${resourceUrl}${query}`;
  }

  public get<T>(resourceUrl: string, optionalParams: HttpOptionalParams = {}): Observable<T> {
    this.requestInProgress = true;

    const httpUrl = this.serializeResourceURl(resourceUrl, optionalParams);

    return this.httpClient.get<T>(httpUrl).pipe(
      tap(() => (this.requestInProgress = false)),
      catchError(err => this._handleError(err))
    );
  }

  public post(resourceUrl: string, optionalParams: any = {}): Observable<Object> {
    this.requestInProgress = true;
    const httpUrl = this.serializeResourceURl(resourceUrl, optionalParams);

    return this.httpClient.post(httpUrl, optionalParams.requestBody).pipe(
      tap(() => (this.requestInProgress = false)),
      catchError(err => this._handleError(err))
    );
  }

  public put(resourceUrl: string, optionalParams: any = {}): Observable<Object> {
    this.requestInProgress = true;
    const httpUrl = this.serializeResourceURl(resourceUrl, optionalParams);

    return this.httpClient.put(httpUrl, optionalParams.requestBody).pipe(
      tap(() => (this.requestInProgress = false)),
      catchError(err => this._handleError(err))
    );
  }

  public delete(resourceUrl: string, optionalParams: any = {}): Observable<Object> {
    this.requestInProgress = true;

    const httpUrl = this.serializeResourceURl(resourceUrl, optionalParams);

    return this.httpClient.delete(httpUrl).pipe(
      tap(() => (this.requestInProgress = false)),
      catchError(err => this._handleError(err))
    );
  }

  public isRequestInProgress() {
    return this.requestInProgress;
  }

  private _getQueryString(queryParamsList: any): string {
    if (!queryParamsList) {
      return '';
    }

    const queryString = queryParamsList
      .map(({ name, value }: QueryParams) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
      .join('&');
    return `?${queryString}`;
  }

  private _handleError(error: any) {
    this.requestInProgress = false;
    let errorMessage = error.json() || 'Server error';

    if (error.status === HTTP_UNAUTHORIZED) {
      // Clear session
      // Go to Home/Login
      errorMessage = 'Session expired';
    }

    return throwError(errorMessage);
  }

  private _setUrlParam(resourceUrl: string, parameter: number | string) {
    if (!parameter) {
      return resourceUrl;
    }

    return resourceUrl.replace(/\/-(?=\/)?/, `/${parameter}`);
  }

  private _setUrlMultiParams(resourceUrl: string, parameters: string[]) {
    if (!parameters) {
      return resourceUrl;
    }
    return resourceUrl.replace(/\/-(?=\/)?/g, () => `/${parameters.shift()}`);
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/http.service.ts